import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Typography
} from "@mui/material";
import InputForm from "../../../../components/controls/InputForm";
import { IconSelector } from "../../../../components/IconSelector";
import * as Yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CreateWorkflowFormItem from "./components/FormItem/form_item";
import { CreateWorkflowDialogWrapper } from "./styles";
import TextEditorQuill from "../../../../components/TextEditorQuill";
import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import Dropdown from "./components/Dropdown";
import { TagInput } from "../TagInput/TagInput";
import WorkflowService from "../../../../services/api/workflow.service";
import { ITag } from "../../../../models/user";
import { toggleMessage } from "../../../../components/Toast/Toast";
import useAxios from "../../../../components/UseAxios/useAxios";
import { ICreateWorkflowRequest, IWorkflow } from "../../../../models/workflow";
import { IStage } from "../../../../models/Stage";
import StageService from "../../../../services/api/stage.service";
import { SearchWorkflow } from "./components/SearchWorkflow/SearchWorkflow";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import variableStyles from "../../../../theme/variable-styles";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { createWorkflow } from "../../../../redux/workflow/workflow.duck";
import { useStore } from "../../../../redux/workflow/workflow_store";

export interface DataForm {
  workflowName: string;
  typeBoard: string;
  icon?: string; // workflow icon name
  management: ITag[];
  assignTasks: ITag[];
  viewAllTasks: ITag[];
  viewTasks: ITag[];
  stageTransitionId: string; //  stage Transition
  stageTransitionToWorkflow: IWorkflow; // workflow position
  stageTransitionToWStageId: string; // stage position
  permission: any[];
  display: any[];
  description: string;
  reasonFailure: string;
}
interface _StageTransition {
  BoardId: string;
  WorkflowId?: string;
  WorkflowRelateds?: _INewWorkflowPosition[];
}
interface _INewWorkflowPosition {
  BoardRelatedId?: string;
  WorkflowRelatedId?: string;
}

interface CreateWorkflowDialogProps {
  open: boolean;
  handleSuccess: () => void;
  handleClose: () => void;
  workflowId?: string;
  typeBoard?: string;
}

/**
 * Component for creating or updating a workflow in a dialog.
 *
 * @component
 * @param {Object} props - The component props
 * @param {boolean} props.open - Controls visibility of the dialog
 * @param {Function} props.handleSuccess - Callback function called on successful creation/update
 * @param {Function} props.handleClose - Function to close the dialog
 * @param {string} [props.workflowId] - ID of workflow being edited (optional, if not provided creates new workflow)
 *
 * @remarks
 * This component handles both creation and editing of workflows with the following features:
 * - Form validation using Yup schema
 * - Rich text editing for description using ReactQuill
 * - Management of workflow stages and transitions
 * - User/group permission management including:
 *   - Process managers
 *   - Task assignment permissions
 *   - View permissions
 * - Workflow stage transition configuration
 * - Error handling and success messages
 * - Loading states
 *
 * @example
 * ```tsx
 * <CreateWorkflowDialog
 *   open={dialogOpen}
 *   handleSuccess={() => {
 *     // Handle success
 *   }}
 *   handleClose={() => setDialogOpen(false)}
 *   workflowId="workflow-123" // Optional
 * />
 * ```
 */
const CreateWorkflowDialog = ({
  open,
  handleSuccess,
  handleClose,
  workflowId,
  typeBoard
}: CreateWorkflowDialogProps) => {
  const quillRef = useRef<ReactQuill>(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const setValueWorkflowStore = useStore(state => state.setValue);

  const createWorkflowAxios = useAxios<string>({ loading: "OnRequest" });
  const updateWorkflowAxios = useAxios<string>({ loading: "OnRequest" });
  const getWorkflowAxios = useAxios<IWorkflow>({ loading: "OnRequest" });

  const getStagesCurrentWorkflowAxios = useAxios<IStage[]>({
    loading: "OnRequest"
  });

  const getStagesNextWorkflowAxios = useAxios<IStage[]>({
    loading: "OnRequest"
  });

  const [updateLoaded, setUpdateLoaded] = useState<boolean>(false);
  const [stageTransition, setStageTransition] = useState<_StageTransition>({
    BoardId: (workflowId ?? "") as string
  });

  useEffect(() => {
    if (open === true) {
      reset();
      setUpdateLoaded(false);
      typeBoard && setValue("typeBoard", typeBoard);

      if (workflowId) {
        getWorkflowAxios.request(WorkflowService.getWorkflow(workflowId));
        getStagesCurrentWorkflowAxios.request(
          StageService.getListStageByWorkflowId(workflowId)
        );
      }
    }
    return () => {
      createWorkflowAxios.reset();
      getWorkflowAxios.reset();
      updateWorkflowAxios.reset();
    };
  }, [open, workflowId]);

  useEffect(() => {
    if (getWorkflowAxios.data) {
      const workflow = getWorkflowAxios.data;
      reset({
        workflowName: workflow.name,
        typeBoard: workflow.typeBoard,
        icon: workflow.icon ?? undefined,
        management: workflow.processManagements.map(e => ({
          id: e.id,
          userName: e.userName,
          type: e.type
        })),
        assignTasks: workflow.assignTasks.map(e => ({
          id: e.id,
          userName: e.userName,
          type: e.type
        })),
        viewAllTasks: workflow.viewAllTasks.map(e => ({
          id: e.id,
          userName: e.userName,
          type: e.type
        })),
        viewTasks: workflow.viewTasks.map(e => ({
          id: e.id,
          userName: e.userName,
          type: e.type
        })),
        stageTransitionId: workflow.stageTransition?.workflowId,
        stageTransitionToWorkflow: workflow.stageTransition?.workflowRelateds[0]
          ? {
              name: workflow.stageTransition.workflowRelateds[0].boardName,
              id: workflow.stageTransition.workflowRelateds[0].boardRelatedId
            }
          : undefined,
        stageTransitionToWStageId:
          workflow.stageTransition?.workflowRelateds?.[0]?.workflowRelatedId,
        description: workflow.description ?? undefined,
        reasonFailure: workflow.reasonForFailure
      });

      if (workflow.stageTransition) {
        setStageTransition({
          BoardId: workflow.stageTransition.boardId,
          WorkflowId: workflow.stageTransition.workflowId,
          WorkflowRelateds: workflow.stageTransition.workflowRelateds.map(
            e => ({
              BoardRelatedId: e.boardRelatedId,
              WorkflowRelatedId: e.workflowRelatedId
            })
          )
        });
      }
      setUpdateLoaded(true);
    }

    if (getWorkflowAxios.error) {
      toggleMessage({
        type: "error",
        message: getWorkflowAxios.error.message ?? ""
      });
      handleClose();
    }
  }, [getWorkflowAxios.data, getWorkflowAxios.error]);

  const handleCloseAndClear = () => {
    handleClose();
    reset(); // reset toàn bộ form về defaultValues
  };

  const validationSchema = Yup.object().shape({
    workflowName: Yup.string()
      .trim()
      .required(`${t("Management.PleaseEnterTheWorkflowName")}`),
    typeBoard: Yup.string().required(
      `${t("Management.PleaseSelectTheWorkflowCategory")}`
    ),
    stageTransitionId: Yup.string().nullable(),
    stageTransitionToWorkflow: Yup.mixed<IWorkflow>()
      .nullable()
      .when("stageTransitionId", {
        is: (val: string) => val !== undefined,
        then: schema => schema.required(t("Management.PleaseSelectAWorkflow")),
        otherwise: schema => schema.nullable()
      })
      .when("stageTransitionId", {
        is: (val: string | undefined) => val === undefined,
        then: schema => {
          resetField("stageTransitionToWorkflow");
          return schema.nullable();
        }
      }),
    stageTransitionToWStageId: Yup.string()
      .nullable()
      .when("stageTransitionToWorkflow", {
        is: (val: IWorkflow) => val !== undefined,
        then: schema =>
          schema.required(t("Management.PleaseSelectATransitionStage")),
        otherwise: schema => schema.nullable()
      })
      .when("stageTransitionToWorkflow", {
        is: (val: string | undefined) => val === undefined,
        then: schema => {
          resetField("stageTransitionToWStageId");
          return schema.nullable();
        }
      })
  }) as any;

  const {
    control,
    reset,
    resetField,
    getValues,
    setValue,
    register,
    handleSubmit,
    trigger,
    formState: { errors }
  } = useForm<DataForm>({
    resolver: yupResolver(validationSchema)
  });

  useEffect(() => {
    getStagesNextWorkflowAxios.reset();
    if (getValues("stageTransitionToWorkflow")?.id) {
      getStagesNextWorkflowAxios.request(
        StageService.getListStageByWorkflowId(
          getValues("stageTransitionToWorkflow").id
        )
      );
    }
  }, [getValues("stageTransitionToWorkflow")]);

  useEffect(() => {
    if (createWorkflowAxios.error)
      toggleMessage({
        type: "error",
        message: createWorkflowAxios.error.message ?? ""
      });
    if (createWorkflowAxios.isSuccess) {
      toggleMessage({
        type: "success",
        message: createWorkflowAxios.message
      });
      handleSuccess();
      handleClose();
    }
  }, [createWorkflowAxios.error, createWorkflowAxios.isSuccess]);

  useEffect(() => {
    if (updateWorkflowAxios.error)
      toggleMessage({
        type: "error",
        message: updateWorkflowAxios.error.message ?? ""
      });
    if (updateWorkflowAxios.isSuccess) {
      toggleMessage({
        type: "success",
        message: updateWorkflowAxios.message
      });
      handleSuccess();
      handleClose();
    }
  }, [updateWorkflowAxios.error, updateWorkflowAxios.isSuccess]);

  const onSubmit = async (data: DataForm) => {
    const _req: ICreateWorkflowRequest = {
      TypeBoard: data.typeBoard,
      Name: data.workflowName,
      Description: data.description,
      ReasonForFailure: data.reasonFailure,
      Icon: data.icon,
      ProcessManagements: data.management?.map(v => ({
        id: v.id,
        userName: v.userName,
        type: v.type!
      })),
      AssignTasks: data.assignTasks?.map(v => ({
        id: v.id,
        userName: v.userName,
        type: v.type!
      })),
      ViewAllTasks: data.viewAllTasks?.map(v => ({
        id: v.id,
        userName: v.userName,
        type: v.type!
      })),
      ViewTasks: data.viewTasks?.map(v => ({
        id: v.id,
        userName: v.userName,
        type: v.type!
      }))
    };

    if (workflowId) {
      if (data.stageTransitionId) {
        _req.StageTransition = {
          BoardId: workflowId as string,
          WorkflowId: data.stageTransitionId,
          WorkflowRelateds: [
            {
              BoardRelatedId: data.stageTransitionToWorkflow.id,
              WorkflowRelatedId: data.stageTransitionToWStageId
            }
          ]
        };
      }

      updateWorkflowAxios.request(
        WorkflowService.updateWorkflow(workflowId, _req)
      );
    } else {
      createWorkflowAxios.request(WorkflowService.createWorkflow(_req));

      setValueWorkflowStore(createWorkflowAxios.data);
    }
  };

  return (
    <CreateWorkflowDialogWrapper
      fullWidth
      open={open}
      onClose={handleCloseAndClear}
      scroll={"paper"}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle sx={{ backgroundColor: "#ededed" }}>
        <Box display={"flex"} justifyContent={"space-between"}>
          <Typography fontSize={18} fontWeight={700}>
            {workflowId
              ? t("Management.UpdateWorkflow")
              : t("Management.CreateNewWorkflow")}
          </Typography>
          <Button onClick={handleCloseAndClear} sx={{ color: "grey" }}>
            <Close sx={{ fontSize: 24 }} />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {workflowId && getWorkflowAxios.isLoading && (
          <center>
            <CircularProgress />
          </center>
        )}
        {(!workflowId ||
          (workflowId && getWorkflowAxios.isSuccess && updateLoaded)) && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <CreateWorkflowFormItem
              name={t("Management.WorkflowName")}
              desciption={t("Management.WorkflowName")}
              input={
                <InputForm
                  placeholder={t("Management.WorkflowName")}
                  required
                  name="workflowName"
                  errors={errors}
                  control={control}
                  size="small"
                />
              }
            />
            <CreateWorkflowFormItem
              name={t("Management.WorkflowCategory")}
              desciption={t("Management.WorkflowCategory")}
              input={
                <InputForm
                  placeholder={
                    t("Management.WorkflowCategory") +
                    " (For Example: Marketing, Human Resource)"
                  }
                  required
                  name="typeBoard"
                  errors={errors}
                  control={control}
                  size="small"
                />
              }
            ></CreateWorkflowFormItem>
            <CreateWorkflowFormItem
              name={t("Management.WorkflowAdministrators")}
              desciption={t("Management.ContentWorkflowAdministrators")}
              input={
                <TagInput
                  name={"management"}
                  control={control}
                  errors={errors}
                  defaultValues={getValues("management")}
                  isError={!!errors.management}
                  placeholder={t("Management.PlacehoderGroupCreateTask")}
                />
              }
            ></CreateWorkflowFormItem>
            <CreateWorkflowFormItem
              name={t("Management.GroupCreateTask")}
              desciption={t("Management.ContentGroupCreateTask")}
              input={
                <TagInput
                  name={"assignTasks"}
                  control={control}
                  errors={errors}
                  isError={!!errors.assignTasks}
                  placeholder={t("Management.PlacehoderGroupCreateTask")}
                />
              }
            ></CreateWorkflowFormItem>
            {workflowId && (
              <CreateWorkflowFormItem
                name={t(
                  "Management.AutomaticallyForwardTaskWhenReachingThisStage"
                )}
                desciption={t(
                  "Management.WhenReachingTheSelectedStageTheTaskWillAutomaticallyMoveToAnotherWorkflow"
                )}
                input={
                  <Dropdown
                    name={"stageTransitionId"}
                    control={control}
                    value={getValues("stageTransitionId")}
                    onChange={value => {
                      setStageTransition({
                        BoardId: workflowId as string,
                        WorkflowId: value
                      });

                      if (!value) {
                      }

                      reset({
                        ...getValues(),
                        stageTransitionToWorkflow: undefined,
                        stageTransitionToWStageId: undefined
                      });
                      console.log(getValues());
                      trigger("stageTransitionToWorkflow");
                    }}
                    options={(getStagesCurrentWorkflowAxios.data ?? [])
                      .map((option, index) => (
                        <MenuItem
                          sx={{
                            fontSize: 13,
                            "& .MuiSelect-icon": {
                              fontSize: 24
                            }
                          }}
                          value={option.id}
                          key={`stageTransitionId-item-${index}`}
                        >
                          {option.name}
                        </MenuItem>
                      ))
                      .concat(
                        <MenuItem
                          sx={{
                            fontSize: 13,
                            "& .MuiSelect-icon": {
                              fontSize: 24
                            }
                          }}
                          value={""}
                          key={`stageTransitionId-item-none`}
                        >
                          {t("Management.NoStagesAvailable")}
                        </MenuItem>
                      )}
                    error={errors.stageTransitionId}
                  />
                }
              ></CreateWorkflowFormItem>
            )}
            {stageTransition?.WorkflowId && (
              <CreateWorkflowFormItem
                name={t("Management.SelectWorkflow")}
                icon={
                  <SubdirectoryArrowRightIcon
                    color="primary"
                    sx={{ fontSize: 24 }}
                  />
                }
                desciption={""}
                input={
                  <SearchWorkflow
                    name={"stageTransitionToWorkflow"}
                    control={control}
                    errors={errors}
                    isError={!!errors.stageTransitionToWorkflow}
                    value={getValues("stageTransitionToWorkflow")}
                    onChange={e => {
                      console.log("2", e);
                      setStageTransition({
                        ...stageTransition,
                        WorkflowRelateds: [
                          {
                            BoardRelatedId: e?.id
                          }
                        ]
                      });
                      reset({
                        ...getValues(),
                        stageTransitionToWStageId: undefined
                      });
                      trigger("stageTransitionToWorkflow");
                      trigger("stageTransitionToWStageId");
                    }}
                    placeholder={t("Management.SelectWorkflow")}
                  />
                }
              ></CreateWorkflowFormItem>
            )}
            {stageTransition?.WorkflowRelateds?.[0]?.BoardRelatedId && (
              <CreateWorkflowFormItem
                name={t("Management.SelectTransitionStage")}
                icon={
                  <SubdirectoryArrowRightIcon
                    color="primary"
                    sx={{ marginLeft: 2, fontSize: 24 }}
                  />
                }
                desciption={""}
                input={
                  <Dropdown
                    name={"stageTransitionToWStageId"}
                    control={control}
                    value={getValues("stageTransitionToWStageId")}
                    onChange={value => {
                      setStageTransition({
                        ...stageTransition!,
                        WorkflowRelateds: [
                          {
                            BoardRelatedId:
                              stageTransition?.WorkflowRelateds?.[0]
                                ?.BoardRelatedId,
                            WorkflowRelatedId: value
                          }
                        ]
                      });
                      trigger("stageTransitionToWStageId");
                    }}
                    options={(getStagesNextWorkflowAxios.data ?? []).map(
                      (option, index) => (
                        <MenuItem
                          sx={{ fontSize: 13 }}
                          value={option.id}
                          key={`stageTransitionToWStageId-item-${index}`}
                        >
                          {option.name}
                        </MenuItem>
                      )
                    )}
                    error={errors.stageTransitionToWStageId}
                  />
                }
              ></CreateWorkflowFormItem>
            )}

            <Divider>{t("Management.OtherOptions")}</Divider>

            {/* <CreateWorkflowFormItem
              name={"Tuỳ chọn quyền xem các nhiệm vụ"}
              desciption={"Quyền xem các nhiệm vụ trong luồng công việc"}
              input={
                <Controller
                  control={control}
                  name={"permission"}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={permissionOptions}
                      values={field.value}
                    />
                  )}
                />
              }
            ></CreateWorkflowFormItem> */}
            {/* <CreateWorkflowFormItem
              name={"Tuỳ chọn hiển thị các nhiệm vụ"}
              desciption={
                "Tuỳ chọn hiển thị các nhiệm vụ trong luồng công việc"
              }
              input={
                <Controller
                  control={control}
                  name={"display"}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={displayOptions}
                      values={field.value}
                    />
                  )}
                />
              }
            ></CreateWorkflowFormItem> */}
            <CreateWorkflowFormItem
              name={t("Management.WorkflowSupervisor")}
              desciption={t("Management.ContentWorkflowSupervisor")}
              input={
                <TagInput
                  name={"viewAllTasks"}
                  control={control}
                  errors={errors}
                  isError={!!errors.viewAllTasks}
                  placeholder=""
                />
              }
            ></CreateWorkflowFormItem>
            <CreateWorkflowFormItem
              name={t("Management.MemberGroupsWithPermissionToWiewTheWorkflow")}
              desciption={t(
                "Management.MembersOrMemberGroupsWhoCanSeeTheWorkflowInTheList"
              )}
              input={
                <TagInput
                  name={"viewTasks"}
                  control={control}
                  errors={errors}
                  isError={!!errors.viewTasks}
                  placeholder=""
                />
              }
            ></CreateWorkflowFormItem>
            <CreateWorkflowFormItem
              name={t("Management.WorkflowUserGuide")}
              desciption={t("Management.DetailedDescriptionOfTheWorkflow")}
              input={
                <Controller
                  control={control}
                  name={"description"}
                  render={({ field }) => (
                    <TextEditorQuill
                      {...field}
                      placeholder={t("Management.DetailedWorkflowDescription")}
                      toolbar={true}
                      editorStyle={{
                        border: "1px solid #D3DCDF",
                        borderRadius: "8px",
                        width: "100%",
                        height: "24vh"
                      }}
                      quillRef={quillRef}
                    />
                  )}
                />
              }
            ></CreateWorkflowFormItem>

            <CreateWorkflowFormItem
              name={t("Management.WorkflowIconName")}
              desciption={t("Management.WorkflowIconDescription")}
              input={
                <IconSelector
                  value={getValues("icon") || ""}
                  onChange={(svg, iconName, label) => {
                    setValue("icon", svg);
                  }}
                  placeholder={t("Management.WorkflowIconPlaceholder")}
                />
              }
            ></CreateWorkflowFormItem>

            <Divider>{t("Management.ReasonForTaskFailure")}</Divider>

            <CreateWorkflowFormItem
              name={t("Management.ReasonForTaskFailure")}
              desciption={t(
                "Management.PleaseProvideDetailedPossibleReasonsForTaskFailureIfAny"
              )}
              input={
                <InputForm
                  placeholder={t("Management.EnterAPossibleReasonForFailure")}
                  required
                  name="reasonFailure"
                  errors={errors}
                  control={control}
                  size="small"
                />
              }
            ></CreateWorkflowFormItem>
          </form>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button
          fullWidth
          variant="contained"
          color="inherit"
          onClick={
            createWorkflowAxios.isLoading === false &&
            updateWorkflowAxios.isLoading === false &&
            getWorkflowAxios.isLoading === false
              ? handleClose
              : undefined
          }
          sx={{ height: 40, borderRadius: 1.5, fontSize: 14, fontWeight: 700 }}
        >
          {t("Common.Close")}
        </Button>
        <Box width={24}></Box>
        <Button
          fullWidth
          disabled={
            createWorkflowAxios.isLoading ||
            updateWorkflowAxios.isLoading ||
            getWorkflowAxios.isLoading
          }
          sx={{
            height: 40,
            borderRadius: 1.5,
            fontSize: 14,
            fontWeight: 700,
            backgroundColor: variableStyles.MainColor
          }}
          variant="contained"
          type="submit"
          onClick={
            createWorkflowAxios.isLoading === false &&
            updateWorkflowAxios.isLoading === false &&
            getWorkflowAxios.isLoading === false
              ? handleSubmit(onSubmit)
              : undefined
          }
        >
          {createWorkflowAxios.isLoading || updateWorkflowAxios.isLoading ? (
            <CircularProgress color="secondary" size={24} />
          ) : workflowId ? (
            t("Management.UpdateWorkflow")
          ) : (
            t("Management.CreateNewWorkflow")
          )}
        </Button>
      </DialogActions>
    </CreateWorkflowDialogWrapper>
  );
};

export default CreateWorkflowDialog;
